import {
  Avatar,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import { foyerApi, type RadioStationRecord } from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { useCan } from "../auth/AuthContext";
import AssetField from "../components/AssetField";

/**
 * Admin-only global catalogue of Radio Stations. Non-admin users can still
 * GET the list (read-only), but only admins reach this page (nav item is
 * hidden) and the mutation endpoints reject anyone else with 403.
 *
 * Premises consume this catalogue: per-premise selection + overrides are
 * resolved against it in `shared/radio-merge.ts`.
 */
export default function RadioStationsPage() {
  // Mutation affordances follow the same permission the API enforces.
  const isAdmin = useCan("canManageRadioStations");
  const [items, setItems] = useState<RadioStationRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; record: RadioStationRecord } | null
  >(null);

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listRadioStations();
      setItems(Array.isArray(list) ? list : []);
      setErr(null);
    } catch (e) {
      setErr(friendlyError(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onDelete(row: RadioStationRecord) {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await foyerApi.deleteRadioStation(row.id);
      await refresh();
    } catch (e) {
      setErr(friendlyError(e));
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Catalogue"
        title="Radio stations"
        actions={
          isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ mode: "create" })}
              data-testid="radio-station-add"
            >
              New station
            </Button>
          )
        }
      />

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="radio-stations-list">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell>Name</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Stream URL</TableCell>
                {isAdmin && <TableCell sx={{ width: 96 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  data-testid={`radio-station-row-${row.id}`}
                >
                  <TableCell>
                    <Avatar
                      src={row.logoUrl ?? undefined}
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "grey.600",
                        fontSize: 14,
                      }}
                    >
                      {row.name.charAt(0)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.tag}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      title={row.streamUrl ?? ""}
                    >
                      {row.streamUrl ?? "-"}
                    </Typography>
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          aria-label={`Edit ${row.name}`}
                          onClick={() =>
                            setDialog({ mode: "edit", record: row })
                          }
                          data-testid={`radio-station-edit-${row.id}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          aria-label={`Delete ${row.name}`}
                          onClick={() => onDelete(row)}
                          data-testid={`radio-station-delete-${row.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4}>
                    <Typography variant="caption" color="text.secondary">
                      No radio stations yet. Click <strong>New station</strong>{" "}
                      to add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {dialog && (
        <RadioStationDialog
          initial={dialog.mode === "edit" ? dialog.record : null}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
        />
      )}
    </PageContainer>
  );
}

function RadioStationDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: RadioStationRecord | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [tag, setTag] = useState(initial?.tag ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");
  const [streamUrl, setStreamUrl] = useState(initial?.streamUrl ?? "");
  const [metadataId, setMetadataId] = useState(initial?.metadataId ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        name,
        tag,
        logoUrl: logoUrl.trim() || null,
        streamUrl: streamUrl.trim() || null,
        metadataId: metadataId.trim() || null,
      };
      if (initial) {
        await foyerApi.updateRadioStation(initial.id, payload);
      } else {
        await foyerApi.createRadioStation(payload);
      }
      await onSaved();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial ? "Edit station" : "New station"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            autoFocus
            placeholder="P1"
            inputProps={{ "data-testid": "radio-station-name" }}
          />
          <TextField
            label="Tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            size="small"
            placeholder="Talk · News"
          />
          <AssetField
            label="Logo"
            value={logoUrl}
            onChange={setLogoUrl}
            preview
            testId="radio-station-logo"
          />
          <TextField
            label="Stream URL"
            value={streamUrl}
            onChange={(e) => setStreamUrl(e.target.value)}
            size="small"
            placeholder="https://…"
          />
          <TextField
            label="Metadata ID (Sveriges Radio)"
            value={metadataId}
            onChange={(e) => setMetadataId(e.target.value)}
            size="small"
          />
          {err && (
            <Typography variant="caption" color="error">
              {err}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || !name.trim()}
          data-testid="radio-station-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
